(() => {
  if (customElements.get('welcome-popup')) return;

  const STORAGE_KEYS = {
    subscribed: 'sylavieWelcomePopupSubscribed',
    dismissedUntil: 'sylavieWelcomePopupDismissedUntil',
    sessionShown: 'sylavieWelcomePopupSessionShown',
  };

  const INTERACTION_EVENTS = ['scroll', 'mousemove', 'touchstart'];
  const CLOSE_DURATION = 380;

  class WelcomePopup extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('.welcome-popup__dialog');
      this.emailInput = this.querySelector('input[type="email"]');
      this.form = this.closest('[data-welcome-popup-form]');
      this.previouslyFocused = null;
      this.showTimer = null;
      this.interactionController = null;
      this.inertedElements = [];
      this.isOpen = false;
      this.hasInteracted = false;
      this.pageReadyAt = performance.now();
      this.handleKeyup = this.handleKeyup.bind(this);
      this.handleInteraction = this.handleInteraction.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      if (this.connectionActive) return;
      this.connectionActive = true;

      if (this.dataset.initialized !== 'true') {
        this.dataset.initialized = 'true';
        this.querySelectorAll('[data-welcome-popup-dismiss]').forEach((button) => {
          button.addEventListener('click', () => this.close({ persistDismissal: true }));
        });
        this.querySelector('[data-welcome-popup-continue]')?.addEventListener('click', () => this.close());
        this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      }

      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);

      const hasFormResponse = this.dataset.formSuccess === 'true' || this.dataset.formError === 'true';
      if (hasFormResponse && !this.isOwnFormTarget()) return;

      if (hasFormResponse) this.clearPropagatedFormState();

      if (this.dataset.formSuccess === 'true') {
        this.writeStorage(localStorage, STORAGE_KEYS.subscribed, 'true');
        this.emit('sylavie:welcome-signup-success', {
          provider: 'shopify',
        });
        this.open({ force: true });
        return;
      }

      if (this.dataset.formError === 'true') {
        this.open({ force: true });
        return;
      }

      if (this.shouldOpenPreview()) {
        this.open({ force: true });
        return;
      }

      if (this.isEditor()) return;
      this.scheduleEligibility();
    }

    disconnectedCallback() {
      this.connectionActive = false;
      this.clearEligibility();
      document.removeEventListener('keyup', this.handleKeyup);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.restoreBackground();
    }

    scheduleEligibility() {
      if (!this.isEligible()) return;

      this.interactionController = new AbortController();
      INTERACTION_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, this.handleInteraction, {
          passive: true,
          signal: this.interactionController.signal,
        });
      });
    }

    handleInteraction() {
      if (this.hasInteracted) return;
      this.hasInteracted = true;
      this.interactionController?.abort();

      const delay = Math.max(Number(this.dataset.delaySeconds) || 9, 0) * 1000;
      const remainingDelay = Math.max(delay - (performance.now() - this.pageReadyAt), 0);
      this.showTimer = window.setTimeout(() => this.tryToOpen(), remainingDelay);
    }

    tryToOpen() {
      this.showTimer = null;
      if (!this.hasInteracted || !this.isEligible()) return;

      if (this.hasBlockingUi()) {
        this.showTimer = window.setTimeout(() => this.tryToOpen(), 1000);
        return;
      }

      this.open();
    }

    isEligible() {
      if (this.dataset.enabled !== 'true' || this.isCheckout()) return false;
      if (this.readStorage(localStorage, STORAGE_KEYS.subscribed) === 'true') return false;
      if (this.readStorage(sessionStorage, STORAGE_KEYS.sessionShown) === 'true') return false;

      const dismissedUntil = Number(this.readStorage(localStorage, STORAGE_KEYS.dismissedUntil) || 0);
      return !dismissedUntil || Date.now() >= dismissedUntil;
    }

    hasBlockingUi() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.classList.contains('active')) return true;

      return Array.from(
        document.querySelectorAll(
          '[role="dialog"][aria-modal="true"], dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], .modal[open], .size-guide-modal[open], details[open] > .modal__content'
        )
      ).some((modal) => modal !== this.dialog && !this.contains(modal) && this.isVisible(modal));
    }

    isVisible(element) {
      if (!(element instanceof HTMLElement)) return false;
      const styles = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return (
        !element.hidden &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.opacity !== '0' &&
        (rect.width > 0 || rect.height > 0)
      );
    }

    open({ force = false } = {}) {
      if (this.isOpen || (!force && (!this.isEligible() || this.hasBlockingUi()))) return;

      this.clearEligibility();
      this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.hidden = false;
      this.isOpen = true;
      this.makeBackgroundInert();
      document.body.classList.add('welcome-popup-open');
      if (!this.isPreviewMode()) this.writeStorage(sessionStorage, STORAGE_KEYS.sessionShown, 'true');
      document.addEventListener('keyup', this.handleKeyup);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-visible');
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, this.emailInput || this.dialog);
          } else {
            (this.emailInput || this.dialog).focus({ preventScroll: true });
          }
        });
      });

      this.emit('sylavie:welcome-popup-opened');
    }

    close({ persistDismissal = false } = {}) {
      if (!this.isOpen) return;
      if (persistDismissal && this.dataset.formSuccess !== 'true' && !this.isPreviewMode()) {
        const dismissDays = Math.max(Number(this.dataset.dismissDays) || 30, 1);
        const dismissedUntil = Date.now() + dismissDays * 24 * 60 * 60 * 1000;
        this.writeStorage(localStorage, STORAGE_KEYS.dismissedUntil, String(dismissedUntil));
      }

      this.isOpen = false;
      this.classList.remove('is-visible');
      document.removeEventListener('keyup', this.handleKeyup);
      document.body.classList.remove('welcome-popup-open');
      this.restoreBackground();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.previouslyFocused);
      } else {
        this.previouslyFocused?.focus({ preventScroll: true });
      }

      window.setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, CLOSE_DURATION);

      this.emit('sylavie:welcome-popup-closed', { dismissed: persistDismissal });
    }

    handleKeyup(event) {
      if (event.code === 'Escape') this.close({ persistDismissal: true });
    }

    getBodyChild() {
      let bodyChild = this;
      while (bodyChild.parentElement && bodyChild.parentElement !== document.body) {
        bodyChild = bodyChild.parentElement;
      }

      return bodyChild;
    }

    makeBackgroundInert() {
      const activeBodyChild = this.getBodyChild();
      this.inertedElements = Array.from(document.body.children).filter(
        (element) => element !== activeBodyChild && !element.inert
      );
      this.inertedElements.forEach((element) => {
        element.inert = true;
      });
    }

    restoreBackground() {
      this.inertedElements.forEach((element) => {
        element.inert = false;
      });
      this.inertedElements = [];
    }

    handleSubmit(event) {
      const integrationEvent = this.emit(
        'sylavie:welcome-signup-submit',
        {
          email: this.emailInput?.value || '',
          provider: 'shopify',
          form: this.form,
        },
        true
      );

      if (integrationEvent.defaultPrevented) event.preventDefault();
    }

    isOwnFormTarget() {
      if (!this.form?.id || !window.location.hash) return false;

      try {
        return decodeURIComponent(window.location.hash.slice(1)) === this.form.id;
      } catch (error) {
        return false;
      }
    }

    clearPropagatedFormState() {
      document.querySelectorAll('.newsletter-form:not(:target)').forEach((form) => {
        form.querySelectorAll('[autofocus]').forEach((element) => element.removeAttribute('autofocus'));
        form.querySelectorAll('[aria-invalid="true"]').forEach((element) => {
          element.removeAttribute('aria-invalid');
          element.removeAttribute('aria-describedby');
        });
      });
    }

    handleSectionSelect(event) {
      if (event.detail.sectionId === this.dataset.sectionId) this.open({ force: true });
    }

    handleSectionDeselect(event) {
      if (event.detail.sectionId === this.dataset.sectionId) this.close();
    }

    clearEligibility() {
      if (this.showTimer) window.clearTimeout(this.showTimer);
      this.showTimer = null;
      this.interactionController?.abort();
      this.interactionController = null;
    }

    isCheckout() {
      return /^\/checkouts?(\/|$)/.test(window.location.pathname);
    }

    isEditor() {
      return this.dataset.shopifyEditor === 'true' || Boolean(window.Shopify?.designMode);
    }

    isPreviewMode() {
      return this.hasPreviewParameter() || this.isEditor();
    }

    shouldOpenPreview() {
      return this.hasPreviewParameter() || (this.isEditor() && this.dataset.previewInEditor === 'true');
    }

    hasPreviewParameter() {
      return new URLSearchParams(window.location.search).get('welcome_popup_preview') === '1';
    }

    readStorage(storage, key) {
      try {
        return storage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    writeStorage(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (error) {
        // Storage can be unavailable in privacy modes; the popup still functions for the current page.
      }
    }

    emit(name, detail = {}, cancelable = false) {
      const event = new CustomEvent(name, { bubbles: true, cancelable, detail });
      this.dispatchEvent(event);
      return event;
    }
  }

  customElements.define('welcome-popup', WelcomePopup);
})();
